import React, { FunctionComponent } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { RealocacaoItem, formatarNumero } from '../types/RealocacaoPedidosMpItens.types';

const useStyles = makeStyles({
  drawer: {
    width: 700,
    flexShrink: 0,
  },
  drawerPaper: {
    width: 700,
    padding: '24px',
    backgroundColor: '#fafafa',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #ebebeb',
  },
  title: {
    fontWeight: 700,
    fontSize: '18px',
    color: '#1a1a1a',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '10px',
    border: '1px solid #ebebeb',
    maxHeight: 'calc(100vh - 120px)',
  },
  tableHeader: {
    backgroundColor: '#f9f9f9',
  },
  tableHeaderCell: {
    fontWeight: 700,
    fontSize: '11px',
    color: '#555',
    textTransform: 'uppercase',
    padding: '12px 16px',
    borderBottom: '2px solid #ebebeb',
    whiteSpace: 'nowrap',
  },
  tableCell: {
    fontSize: '12px',
    color: '#2a2a2a',
    padding: '12px 16px',
    borderBottom: '1px solid #ebebeb',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#fcfcfc',
    },
    '&:last-child td': {
      borderBottom: 'none',
    },
  },
});

interface ItensRealocacaoDrawerProps {
  open: boolean;
  onClose: () => void;
  item: RealocacaoItem | null;
}

const formatarDataLocal = (data?: Date | string | null): string => {
  if (!data) return '—';
  const dateObj = typeof data === 'string' ? new Date(data) : data;
  if (isNaN(dateObj.getTime())) return '—';
  return dateObj.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const ItensRealocacaoDrawer: FunctionComponent<ItensRealocacaoDrawerProps> = ({
  open,
  onClose,
  item,
}) => {
  const classes = useStyles();

  if (!item) return null;

  const campos = [
    { label: 'ID Item', value: item.id_item },
    { label: 'Ordem Produção', value: item.ordemProducao },
    { label: 'Programação', value: item.programacao },
    { label: 'Produto', value: item.produto },
    { label: 'Facção Produção', value: item.faccaoProducao },
    { label: 'Facção Pedido Triangulado', value: item.faccaoPedidoTriangulado },
    { label: 'Reserva Final', value: formatarNumero(item.reservaFinal) },
    { label: 'Quantidade Realocar', value: formatarNumero(item.quantidadeRealocar) },
    { label: 'Pedido MP', value: item.pedidoMp },
    { label: 'Material', value: item.material },
    { label: 'Descrição Material', value: item.descMaterial },
    { label: 'Cor Material', value: item.corMaterial },
    { label: 'Descrição Cor Material', value: item.descCorMaterial },
    { label: 'Rede Lojas', value: item.descRedeLojas },
    { label: 'Time Responsável', value: item.time },
    { label: 'Compradora', value: item.compradora },
    { label: 'Responsável PCP', value: item.responsavelPcp },
    { label: 'Fornecedor MP', value: item.fornecedorMp },
    { label: 'Data Emissão Pedido', value: formatarDataLocal(item.dataEmissaoPedido) },
    { label: 'Data Faturamento', value: formatarDataLocal(item.dataFaturamento) },
    { label: 'Status Faturamento', value: item.faturado },
  ];

  return (
    <Drawer
      className={classes.drawer}
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <Box className={classes.header}>
        <Typography className={classes.title}>Detalhes do Item</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <TableContainer className={classes.tableContainer} component={Paper} elevation={0}>
        <Table stickyHeader>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell className={classes.tableHeaderCell}>Campo</TableCell>
              <TableCell className={classes.tableHeaderCell}>Valor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campos.map((campo, index) => (
              <TableRow key={index} className={classes.tableRow}>
                <TableCell className={classes.tableCell} style={{ fontWeight: 600 }}>
                  {campo.label}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {campo.value || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Drawer>
  );
};

export default ItensRealocacaoDrawer;
